'use client';

import { AUTH_PROVIDER, SESSION_STATUS } from '@/constants/auth';
import { useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useSession, signIn } from 'next-auth/react';
import { Alert } from '@mui/material';
import { Box, Button, Grid, Stack, Typography } from '@/primitives';
import { blue, slate } from '@/lib/colours';
import { usePrivate } from '@/components/private/PrivateContext';
import { useLogout } from '@/hooks/useLogout';
import { APP_ROUTE_PATHS } from '@/configs/routes';
import { CircularProgress } from '@/primitives';
import {
  heroAccentGlow,
  heroSectionBg,
  sectionShell,
} from '@/components/home/homeLandingSectionSx';

const steps = [
  {
    number: '1',
    headline: 'Contact your Breed Society',
    description:
      "Ask your breed society's registrar or administrator to grant you Registry access.",
  },
  {
    number: '2',
    headline: 'Receive your invitation',
    description:
      "You'll get an email to set up your BreedLOGIC account and accept the invite.",
  },
  {
    number: '3',
    headline: 'Sign in and get started',
    description:
      'Log in to Registry and manage your animals, members and registrations.',
  },
];

/** When authenticated with at least one org (registry access), redirect to dashboard so user is sent to org-scoped registry. */
function useRedirectToRegistryIfHasAccess() {
  const router = useRouter();
  const { status } = useSession();
  const { userOrganisations } = usePrivate();

  useEffect(() => {
    if (status !== SESSION_STATUS.AUTHENTICATED) return;
    // Wait until org list has been loaded (undefined = still loading from PostLoginFlow)
    if (userOrganisations === undefined) return;
    if (userOrganisations.length > 0) {
      router.replace(APP_ROUTE_PATHS.DASHBOARD);
    }
  }, [router, status, userOrganisations]);
}

const signInButtonSx = {
  px: 3,
  py: 1.25,
  borderRadius: '10px',
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '1rem',
  backgroundColor: blue[500],
  boxShadow: `0 12px 40px -12px ${blue[600]}aa`,
  '&:hover': { backgroundColor: blue[400] },
};

/** Matches `public/images/registry-landing-card.png` (1024×532). */
const REGISTRY_CARD_IMAGE = '/images/registry-landing-card.png';
const REGISTRY_CARD_ASPECT_RATIO = '1024 / 532';

const signOutButtonSx = {
  px: 3,
  py: 1.25,
  borderRadius: '10px',
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '1rem',
  borderColor: slate[400],
  color: slate[800],
  '&:hover': {
    borderColor: slate[500],
    backgroundColor: slate[100],
  },
};

/** Panel behind “How it works”: slightly darker than flat white, subtle cool gradient. */
const howItWorksPanelBackground = {
  backgroundColor: slate[100],
  backgroundImage: `
    radial-gradient(ellipse 95% 75% at 100% 0%, ${blue[100]}50 0%, transparent 52%),
    linear-gradient(
      168deg,
      ${slate[200]}35 0%,
      ${slate[100]} 32%,
      ${slate[50]} 58%,
      ${slate[200]}22 100%
    )
  `,
  boxShadow: `inset 0 1px 0 ${slate[300]}40`,
};

function HowItWorksPanel() {
  return (
    <Box
      sx={{
        borderRadius: '16px',
        border: `1px solid ${slate[600]}66`,
        ...howItWorksPanelBackground,
        p: { xs: 2.5, sm: 3, md: 2.25 },
        width: '100%',
        minWidth: 0,
        minHeight: { xs: 'auto', md: '100%' },
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        flex: { md: 1 },
      }}
    >
      <Stack
        spacing={{ xs: 1, md: 0.5 }}
        sx={{
          flexShrink: 0,
          pb: { xs: 2, sm: 2.5, md: 2.5 },
          mb: { xs: 2, sm: 2.5, md: 2 },
          textAlign: { xs: 'center', md: 'left' },
        }}
      >
        <Typography
          color={blue[700]}
          sx={{
            fontWeight: 600,
            letterSpacing: '0.08em',
            fontSize: { xs: '0.75rem', md: '0.6875rem' },
          }}
          variant="labelmedium"
        >
          How it works
        </Typography>
        <Typography
          color={slate[900]}
          component="h2"
          id="registry-how-it-works-title"
          sx={{
            maxWidth: { xs: 560, md: 'none' },
            mx: { xs: 'auto', md: 0 },
            fontSize: { md: '1.25rem' },
            lineHeight: { md: 1.35 },
          }}
          variant="headlinelarge"
        >
          Getting started is simple
        </Typography>
      </Stack>
      <Box
        component="ol"
        sx={{
          listStyle: 'none',
          p: 0,
          m: 0,
          flex: { xs: 'none', md: 1 },
          minHeight: { md: 0 },
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, minmax(0, 1fr))',
            md: '1fr',
          },
          gridTemplateRows: {
            md: 'repeat(3, minmax(0, 1fr))',
          },
          columnGap: { sm: 2, md: 0 },
          rowGap: { xs: 3, sm: 0, md: 0 },
          alignContent: { md: 'stretch' },
        }}
      >
        {steps.map(({ number, headline, description }) => (
          <Box
            key={number}
            component="li"
            sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'stretch', md: 'stretch' },
              minHeight: 0,
              height: { md: '100%' },
              width: '100%',
            }}
          >
            {/* No scroll-in animation here — framer `y` transform was leaving step 3 misaligned. */}
            <Stack
              direction={{
                xs: 'column',
                sm: 'column',
                md: 'row',
              }}
              spacing={{ xs: 2, sm: 2, md: 1.5 }}
              sx={{
                alignItems: {
                  xs: 'center',
                  sm: 'center',
                  md: 'flex-start',
                },
                width: '100%',
                minWidth: 0,
                justifyContent: { md: 'flex-start' },
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: { xs: 44, sm: 52, md: 36 },
                  height: { xs: 44, sm: 52, md: 36 },
                  borderRadius: '14px',
                  background: `linear-gradient(145deg, ${blue[600]} 0%, ${blue[700]} 100%)`,
                  boxShadow: {
                    xs: `0 10px 28px -12px ${blue[800]}99`,
                    md: `0 8px 20px -10px ${blue[800]}99`,
                  },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    color: 'common.white',
                    fontSize: {
                      xs: '1.125rem',
                      sm: '1.25rem',
                      md: '0.9375rem',
                    },
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  {number}
                </Typography>
              </Box>
              <Box
                sx={{
                  textAlign: { xs: 'center', sm: 'center', md: 'left' },
                  minWidth: 0,
                }}
              >
                <Typography
                  color={slate[900]}
                  sx={{
                    fontSize: {
                      xs: '1rem',
                      sm: '1.0625rem',
                      md: '0.8125rem',
                    },
                    fontWeight: 600,
                    lineHeight: 1.35,
                    mb: 0.25,
                  }}
                  variant="titlemedium"
                >
                  {headline}
                </Typography>
                <Typography
                  color={slate[600]}
                  sx={{
                    fontSize: {
                      xs: '0.875rem',
                      sm: '0.9375rem',
                      md: '0.75rem',
                    },
                    lineHeight: 1.55,
                  }}
                  variant="bodymedium"
                >
                  {description}
                </Typography>
              </Box>
            </Stack>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

type RegistrySignInCardProps = {
  orgsStillLoading: boolean;
  isAuthenticated: boolean;
  onSignIn: () => void;
  onLogout: () => void | Promise<void>;
};

function RegistrySignInCard({
  orgsStillLoading,
  isAuthenticated,
  onSignIn,
  onLogout,
}: RegistrySignInCardProps) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        border: `1px solid ${slate[700]}`,
        overflow: 'hidden',
        backgroundColor: slate[900],
        boxShadow: '0 24px 64px -28px rgba(0, 0, 0, 0.55)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: REGISTRY_CARD_ASPECT_RATIO,
          flexShrink: 0,
        }}
      >
        <Image
          alt="BreedLOGIC Registry: animals list with cattle in the background"
          fill
          sizes="(max-width: 900px) 100vw, 50vw"
          src={REGISTRY_CARD_IMAGE}
          style={{ objectFit: 'contain' }}
        />
      </Box>
      {/* Fills extra row height when the How it works card is taller (Grid stretch). */}
      <Box
        aria-hidden
        sx={{ flex: 1, minHeight: 0, backgroundColor: slate[900] }}
      />
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.75, md: 2 },
          backgroundColor: slate[50],
          borderTop: `1px solid ${slate[200]}`,
          flexShrink: 0,
        }}
      >
        {orgsStillLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {!orgsStillLoading && isAuthenticated && (
          <>
            <Alert
              severity="info"
              sx={{
                mb: 2,
                borderRadius: '12px',
                border: `1px solid ${blue[200]}`,
                backgroundColor: blue[50],
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: '22px',
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Your account doesn&apos;t have Registry access yet.
              </Typography>
              <Typography sx={{ fontSize: '13px', lineHeight: '20px' }}>
                Contact your Breed Society to request access. If your society
                hasn&apos;t yet migrated to BreedLOGIC, check back soon.
              </Typography>
            </Alert>
            <Button
              fullWidth
              variant="outlined"
              sx={signOutButtonSx}
              onClick={onLogout}
            >
              Sign out
            </Button>
          </>
        )}
        {!orgsStillLoading && !isAuthenticated && (
          <Button
            fullWidth
            size="large"
            variant="contained"
            sx={signInButtonSx}
            onClick={onSignIn}
          >
            Sign in to Registry
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default function RegistryLandingSection() {
  const { status } = useSession();
  const { userOrganisations } = usePrivate();
  const { logout } = useLogout();

  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;

  useRedirectToRegistryIfHasAccess();

  const orgsStillLoading = isAuthenticated && userOrganisations === undefined;

  function handleSignIn() {
    signIn(AUTH_PROVIDER);
  }

  return (
    <Box component="article" sx={{ width: '100%' }}>
      <Box
        component="section"
        aria-labelledby="registry-landing-title"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 6, md: 10 },
          pb: { xs: 8, md: 12 },
          ...heroSectionBg,
        }}
      >
        <Box
          aria-hidden
          sx={{
            ...heroAccentGlow,
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
          }}
        />
        <Box sx={{ ...sectionShell, position: 'relative', zIndex: 1 }}>
          <Stack
            spacing={3}
            sx={{
              alignItems: 'center',
            }}
          >
            {/* Mimic home `LandingHero`: logo, title, subtitle only */}
            <Box
              sx={{
                position: 'relative',
                width: { xs: 220, sm: 280, md: 320 },
                height: { xs: 46, sm: 59, md: 68 },
              }}
            >
              <Image
                alt="BreedLOGIC"
                fill
                priority
                src="/breedlogic-logo-dark.svg"
                style={{ objectFit: 'contain' }}
              />
            </Box>
            <Typography
              color={slate[50]}
              component="h1"
              id="registry-landing-title"
              sx={{
                textAlign: 'center',
                maxWidth: 880,
                letterSpacing: '-0.02em',
              }}
              variant="displaymedium"
            >
              Registry
            </Typography>
            <Typography
              color={slate[300]}
              component="p"
              sx={{
                textAlign: 'center',
                maxWidth: 640,
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.6,
              }}
            >
              The complete management platform for breed societies — animals,
              members, registrations and more, all in one place.
            </Typography>
            <Typography
              color={slate[200]}
              component="p"
              sx={{
                textAlign: 'center',
                maxWidth: 560,
                mt: { xs: 0.5, md: 1 },
                fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem' },
                lineHeight: 1.55,
                fontWeight: 600,
                letterSpacing: '0.01em',
              }}
            >
              Don&apos;t have a Registry account? Contact your Breed Society to
              request access.
            </Typography>
          </Stack>

          <Grid
            container
            spacing={{ xs: 3, md: 3 }}
            sx={{
              mt: { xs: 3, md: 4 },
              alignItems: 'stretch',
            }}
          >
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ display: 'flex', minWidth: 0 }}
            >
              <RegistrySignInCard
                isAuthenticated={isAuthenticated}
                orgsStillLoading={orgsStillLoading}
                onLogout={logout}
                onSignIn={handleSignIn}
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ display: 'flex', minWidth: 0 }}
            >
              <HowItWorksPanel />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
